import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { intersection, sortBy, without } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  doEngageSendCommunication,
  doEngageSendCommunicationBulk,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectEngageFlatsDataMap,
  selectEngageFlatsItemIds,
  selectEngagePropertiesItem,
} from 'store/remote/engageSelectors';
import { CommunicationType, EngageFlatListItem } from 'types/Engage';
import { EngageContactCard } from './Components/EngageContactCard';
import { EngageSendCommunicationFlatSummary } from './Components/EngageSendCommunicationFlatSummary';

const CommunicationTemplates: {
  id: string;
  name: string;
  description: string;
  type: CommunicationType;
}[] = [
  {
    id: 'resident_survey_request_letter',
    name: 'Survey Request Letter',
    type: CommunicationType.letter,
    description:
      'Letter requesting information about the leaseholders and residents',
  },
  {
    id: 'resident_survey_request_email',
    name: 'Survey Request Email',
    type: CommunicationType.email,
    description:
      'Email requesting information about the leaseholders and residents',
  },
  {
    id: 'resident_survey_confirm_email',
    name: 'Survey Confirm Email',
    type: CommunicationType.email,
    description:
      "Email to request confirmation of residents and leaseholders' details",
  },
];

type FlatContactTypes = {
  owners: boolean;
  ownersWithEmails: boolean;
  residents: boolean;
  residentsWithEmails: boolean;
  primaryOwners: boolean;
  primaryOwnersWithEmails: boolean;
  primaryResidents: boolean;
  primaryResidentsWithEmails: boolean;
};

export type FlatIssues = {
  // 1 warning - Some emails won't be sent
  // 2 error - No emails will be sent
  errorLevel: 0 | 1 | 2;
  noOwners: boolean;
  noOwnersWithEmails: boolean;
  noResidents: boolean;
  noResidentsWithEmails: boolean;
  missingPrimaryOwners: boolean;
  missingPrimaryResidents: boolean;
};

// type CalculatedFlat = EngageCommunicationCalculatedFlat & {
type CalculatedFlat = {
  flat: { id: string; name: string };
  bulkSendIssues?: FlatIssues;
  contactTypes: FlatContactTypes;
};

export const EngageSendCommunicationModal: React.FC<
  ModalScreenProps<ModalScreens.EngageSendCommunication>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { key, contactId, flatIds, propertyId, mode } = route.params;

  const property = useSelector((state) =>
    selectEngagePropertiesItem(state, key, propertyId),
  );

  const allFlatIdsInOrder = useSelector((state) =>
    selectEngageFlatsItemIds(state, key),
  );
  const flatsDataMap = useSelector((state) =>
    selectEngageFlatsDataMap(state, key),
  );

  const isBulkMode = mode === 'bulkFlat';

  const flats = React.useMemo<EngageFlatListItem[]>(
    () =>
      // Intersect with all flat list IDs to preserve order from flat list
      intersection(allFlatIdsInOrder, flatIds)
        .map((flatId) => flatsDataMap[flatId])
        .filter(filterArrayEmpty),
    [allFlatIdsInOrder, flatIds, flatsDataMap],
  );

  const contacts = React.useMemo(
    () => (!isBulkMode ? (flats[0]?.contacts ?? []) : []),
    [flats, isBulkMode],
  );

  const contact = contactId ? contacts.find((c) => c.id === contactId) : null;

  const [communicationId, setCommunicationId] = React.useState<string>();
  const [chosenContactIds, setChosenContactIds] = React.useState<string[]>(
    contact ? [contact.id] : [],
  );
  const [filterCommunicationTypeId, setFilterCommunicationTypeId] =
    React.useState<'email' | 'letter' | 'all'>(contact ? 'email' : 'all');
  const [bulkAudience, setBulkAudience] = React.useState<
    'owners' | 'residents' | 'both' | null
  >(null);
  const [bulkPrimaryOnlySelected, setBulkPrimaryOnlySelected] =
    React.useState<boolean>(false);

  const ownersSelected =
    bulkAudience === 'owners' || bulkAudience === 'both' || false;
  const residentsSelected =
    bulkAudience === 'residents' || bulkAudience === 'both' || false;
  const bulkAudienceOptions: {
    key: 'residents' | 'owners' | 'both';
    label: string;
    description: string;
  }[] = [
    {
      key: 'residents',
      label: 'Residents Only',
      description: 'Contacts who are residents or both',
    },
    {
      key: 'owners',
      label: 'Owners Only',
      description: 'Contacts who are owners or both',
    },
    {
      key: 'both',
      label: 'Owners and Residents',
      description: 'Contacts who are owners, residents or both',
    },
  ];

  const communicationTemplate = communicationId
    ? CommunicationTemplates.find((t) => t.id === communicationId)
    : undefined;

  const contactsWithEmail = React.useMemo(
    () =>
      contact
        ? contact.email
          ? [contact]
          : []
        : contacts.filter((c) => !!c.email),
    [contact, contacts],
  );
  const contactsWithoutEmail = React.useMemo(
    () =>
      contact
        ? contact.email
          ? []
          : [contact]
        : contacts.filter((c) => !c.email),
    [contact, contacts],
  );

  const calculatedFlats = React.useMemo(() => {
    return flats.reduce<{
      error: CalculatedFlat[];
      warning: CalculatedFlat[];
      okay: CalculatedFlat[];
    }>(
      (acc, flat) => {
        // Calculate contact types
        const contactTypes = flat.contacts.reduce<FlatContactTypes>(
          (acc1, contact) => {
            const hasEmail = !!contact.email;
            const isOwner = !!contact.isOwner;
            const isResident = !!contact.isResident;
            const isPrimary = !!contact.isPrimary;
            if (isOwner) {
              acc1.owners = true;
              if (hasEmail) {
                acc1.ownersWithEmails = true;
              }
              if (isPrimary) {
                acc1.primaryOwners = true;
                if (hasEmail) {
                  acc1.primaryOwnersWithEmails = true;
                }
              }
            }
            if (isResident) {
              acc1.residents = true;
              if (hasEmail) {
                acc1.residentsWithEmails = true;
              }
              if (isPrimary) {
                acc1.primaryResidents = true;
                if (hasEmail) {
                  acc1.primaryResidentsWithEmails = true;
                }
              }
            }
            return acc1;
          },
          {
            owners: false,
            ownersWithEmails: false,
            residents: false,
            residentsWithEmails: false,
            primaryOwners: false,
            primaryOwnersWithEmails: false,
            primaryResidents: false,
            primaryResidentsWithEmails: false,
          },
        );

        // Calculate issues
        const issues: FlatIssues = {
          errorLevel: 0,
          noOwners: false,
          noOwnersWithEmails: false,
          noResidents: false,
          noResidentsWithEmails: false,
          missingPrimaryOwners: false,
          missingPrimaryResidents: false,
        };
        const isEmail = communicationTemplate?.type === CommunicationType.email;
        const ownersSelectedForFlat = isEmail && ownersSelected;
        const residentsSelectedForFlat = isEmail && residentsSelected;
        const primaryOnlySelected = isEmail && bulkPrimaryOnlySelected;

        let hasOwnerRecipients = false;
        let hasResidentRecipients = false;

        if (ownersSelectedForFlat) {
          if (!contactTypes.owners) {
            issues.noOwners = true;
          } else if (!contactTypes.ownersWithEmails) {
            issues.noOwnersWithEmails = true;
          } else {
            hasOwnerRecipients = true;
            if (primaryOnlySelected && !contactTypes.primaryOwnersWithEmails) {
              issues.missingPrimaryOwners = true;
            }
          }
        }

        if (residentsSelectedForFlat) {
          if (!contactTypes.residents) {
            issues.noResidents = true;
          } else if (!contactTypes.residentsWithEmails) {
            issues.noResidentsWithEmails = true;
          } else {
            hasResidentRecipients = true;
            if (
              primaryOnlySelected &&
              !contactTypes.primaryResidentsWithEmails
            ) {
              issues.missingPrimaryResidents = true;
            }
          }
        }

        const hasAnyRecipients = hasOwnerRecipients || hasResidentRecipients;
        const hasBlockingIssues =
          issues.noOwners ||
          issues.noOwnersWithEmails ||
          issues.noResidents ||
          issues.noResidentsWithEmails;

        if (ownersSelectedForFlat || residentsSelectedForFlat) {
          if (!hasAnyRecipients) {
            issues.errorLevel = 2;
          } else if (
            hasBlockingIssues ||
            issues.missingPrimaryOwners ||
            issues.missingPrimaryResidents
          ) {
            issues.errorLevel = 1;
          }
        }
        acc[
          issues.errorLevel === 2
            ? 'error'
            : issues.errorLevel === 1
              ? 'warning'
              : 'okay'
        ].push({
          flat,
          contactTypes,
          bulkSendIssues: issues,
        });
        return acc;
      },
      {
        error: [],
        warning: [],
        okay: [],
      },
    );
  }, [
    ownersSelected,
    residentsSelected,
    bulkPrimaryOnlySelected,
    communicationTemplate?.type,
    flats,
  ]);

  const handleSelectContacts = React.useCallback(
    (preset: 'all' | 'owners' | 'residents' | 'none') => {
      if (preset === 'none') {
        setChosenContactIds([]);
        return;
      }
      setChosenContactIds(
        flats.reduce<string[]>(
          (acc, flat) =>
            flat.contacts.reduce((acc1, contact) => {
              if (
                contact.email &&
                (preset === 'owners'
                  ? contact.isOwner
                  : preset === 'residents'
                    ? contact.isResident
                    : true)
              ) {
                acc1.push(contact.id);
              }
              return acc1;
            }, acc),
          [],
        ),
      );
    },
    [flats],
  );

  // Calculate if message can be sent
  let canSend: boolean = false;
  if (communicationTemplate) {
    // Must have chosen message
    if (isBulkMode) {
      // Bulk - must have okay or warning flats
      if (!!calculatedFlats.okay.length || !!calculatedFlats.warning.length) {
        if (communicationTemplate.type === CommunicationType.letter) {
          // Bulk / Letter - okay
          canSend = true;
        } else if (ownersSelected || residentsSelected) {
          // Bulk / Email - must have selected owner or resident
          canSend = true;
        }
      }
    } else if (communicationTemplate.type === CommunicationType.letter) {
      // Single / Letter - must have flats
      canSend = !!flats.length;
    } else {
      // Single / Email - must have provided contact (with email) OR chosen contacts
      canSend = contactId ? !!contact?.email : !!chosenContactIds.length;
    }
  }

  const handleSend = () => {
    if (!communicationId || !property) {
      return;
    }
    if (isBulkMode) {
      dispatch(
        doEngageSendCommunicationBulk({
          key: key,
          communicationId: communicationId,
          propertyId: property.id,
          flatIds: flatIds,
          owners: ownersSelected,
          residents: residentsSelected,
          primaryContactsOnly: bulkPrimaryOnlySelected,
        }),
      );
    } else {
      const [flatId] = flatIds;
      if (!flatId) {
        return;
      }
      dispatch(
        doEngageSendCommunication({
          key: key,
          communicationId: communicationId,
          propertyId: property.id,
          flatId,
          contactIds:
            communicationTemplate?.type === CommunicationType.email
              ? chosenContactIds
              : [],
        }),
      );
    }
  };

  const handleClose = React.useCallback(() => {
    if (communicationTemplate) {
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [communicationTemplate, dispatch]);

  const handleToggleContact = (contactId: string) => {
    if (contact && !isBulkMode) {
      return;
    }
    setChosenContactIds((current) =>
      current.includes(contactId)
        ? without(current, contactId)
        : [...current, contactId],
    );
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !communicationTemplate });
  }, [navigation, communicationTemplate]);
  useEsc(handleClose);

  if (!property) {
    return null;
  }

  return (
    <PageScrollView
      header={
        <>
          <NavigationHeader
            heading="Send Message"
            onPressCancelButton={
              !communicationTemplate ? handleClose : undefined
            }
            onPressBackButton={
              communicationTemplate
                ? () => setCommunicationId(undefined)
                : undefined
            }
            onPressSaveButton={canSend ? handleSend : null}
            saveButtonText="Send"
          />
          {!communicationTemplate && !contactId && (
            <ButtonToggleGroup
              buttons={[
                { text: 'All', key: 'all', disabled: !!contact },
                { text: 'Letters', key: 'letter', disabled: !!contact },
                { text: 'Emails', key: 'email' },
              ]}
              active={filterCommunicationTypeId}
              onActiveChange={(newActive) =>
                setFilterCommunicationTypeId(newActive)
              }
              containerStyle={{
                marginBottom: 12,
                alignSelf: 'stretch',
                marginHorizontal: theme.paddingHorizontal,
              }}
              rounded
              size="small"
            />
          )}
        </>
      }
      pageStyle={{ flex: 1 }}
    >
      {!communicationTemplate && (
        <Card>
          {sortBy(Object.values(CommunicationTemplates), ['ordering'])
            .filter(
              (ct) =>
                filterCommunicationTypeId === 'all' ||
                filterCommunicationTypeId === ct.type,
            )
            .map((ct) => (
              <CommunicationTemplateCardRow
                key={ct.id}
                id={ct.id}
                onPress={setCommunicationId}
              />
            ))}
        </Card>
      )}
      {!!communicationTemplate && (
        <>
          <Card style={{ marginBottom: theme.paddingVertical / 2 }}>
            <CommunicationTemplateCardRow id={communicationTemplate.id} />
          </Card>

          {communicationTemplate.type === CommunicationType.email &&
            (isBulkMode ? (
              /** Bulk / Email */
              <>
                <Card label="Send To" marginBottomQuarter>
                  {bulkAudienceOptions.map((option) => (
                    <CardRow
                      key={option.key}
                      label={option.label}
                      labelSize="small"
                      labelAlignTop
                      radioValue={bulkAudience === option.key}
                      onPress={() => setBulkAudience(option.key)}
                      style={{
                        borderTopEndRadius: 0,
                      }}
                    >
                      <Txt
                        size="xxsmall"
                        style={{ color: theme.colors.fieldValueText }}
                      >
                        {option.description}
                      </Txt>
                    </CardRow>
                  ))}
                </Card>
                <Card>
                  <CardRow
                    label="Primary Contacts Only"
                    labelAlignTop
                    value={
                      bulkAudience
                        ? `If there are no primary contacts, all ${
                            bulkAudience === 'owners'
                              ? 'owners'
                              : bulkAudience === 'residents'
                                ? 'residents'
                                : bulkAudience === 'both'
                                  ? 'owners and residents'
                                  : 'contacts of the selected type'
                          } in the selected flats will receive messages`
                        : undefined
                    }
                    checkedValue={bulkPrimaryOnlySelected}
                    onPress={() =>
                      setBulkPrimaryOnlySelected((current) => !current)
                    }
                  />
                </Card>
              </>
            ) : !contactsWithEmail.length ? (
              /** Single / Email / No Contacts */
              <Card
                label="Send To"
                explanation={
                  contact
                    ? 'This email cannot be sent as the contact does not have an email address'
                    : 'There are no contacts with email addresses'
                }
              >
                {contact ? (
                  <EngageContactCard contact={contact} flatListContact={true} />
                ) : (
                  <CardRow label="This email cannot be sent" />
                )}
              </Card>
            ) : (
              /** Single / Email / Contacts */
              <>
                <Card
                  label="Send To"
                  labelLinkButtons={
                    !contactId
                      ? [
                          {
                            text: 'Select All',
                            onPress: () => handleSelectContacts('all'),
                          },
                          {
                            text: 'Owners',
                            onPress: () => handleSelectContacts('owners'),
                          },
                          {
                            text: 'Residents',
                            onPress: () => handleSelectContacts('residents'),
                          },
                          {
                            text: 'None',
                            onPress: () => handleSelectContacts('none'),
                          },
                        ]
                      : undefined
                  }
                >
                  {contactsWithEmail.map((thisContact) => (
                    <EngageContactCard
                      key={thisContact.id}
                      contact={thisContact}
                      flatListContact={true}
                      checkedValue={
                        contact
                          ? undefined
                          : chosenContactIds?.includes(thisContact.id)
                      }
                      onPress={
                        contact
                          ? undefined
                          : () => handleToggleContact(thisContact.id)
                      }
                      contactDetailsOnOwnLine
                    />
                  ))}
                </Card>
                {!!contactsWithoutEmail.length && (
                  <Card
                    label="Cannot Send To"
                    explanation="These contacts do not have an email address"
                  >
                    {contactsWithoutEmail.map((thisContact) => (
                      <EngageContactCard
                        key={thisContact.id}
                        contact={thisContact}
                        flatListContact={true}
                        contactDetailsOnOwnLine
                      />
                    ))}
                  </Card>
                )}
              </>
            ))}

          <EngageSendCommunicationFlatSummary
            property={property}
            statusGroups={calculatedFlats}
            communicationType={communicationTemplate.type}
            isBulkMode={isBulkMode}
            flatCount={flats.length}
          />
        </>
      )}
    </PageScrollView>
  );
};

const CommunicationTemplateCardRow: React.FC<{
  id: string;
  onPress?: (id: string) => void;
}> = ({ id, onPress }) => {
  const communicationTemplate = CommunicationTemplates.find(
    (c) => c.id === id,
  )!;
  return (
    <CardRow
      key={communicationTemplate.id}
      childrenStyle={{
        flexDirection: 'row',
        columnGap: theme.paddingHorizontal,
        alignItems: 'center',
      }}
      onPress={onPress ? () => onPress(communicationTemplate.id) : undefined}
    >
      <View
        style={{
          borderRadius: 42,
          width: 42,
          height: 42,
          backgroundColor: theme.colors.black10,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {communicationTemplate.type === CommunicationType.email ? (
          <Icon source={ICONS.AtLight} />
        ) : (
          <Icon source={ICONS.FileLight} />
        )}
      </View>
      <View style={{ flex: 1 }}>
        <Txt>{communicationTemplate.name}</Txt>
        {!!communicationTemplate.description && (
          <Txt size="xxsmall" style={{ color: theme.colors.fieldValueText }}>
            {communicationTemplate.description}
          </Txt>
        )}
      </View>
    </CardRow>
  );
};
